import { Box, HStack, useBreakpointValue, VStack } from "@chakra-ui/react"
import { CarouselGallery } from "@pathwright/ux/src/components/gallery/Gallery"
import { COVER_PAGE_PADDING } from "../constants"
import CoverPageDivider from "../CoverPageDivider"
import { useCoverPageContext } from "../CoverPageProvider"
import CoverPageSectionTrigger from "../design/CoverPageSectionTrigger"
import { ImmersiveTriggerButton } from "../immersive/CoverPageImmersive"
import PathPreviewNavigator from "./PathPreviewNavigator"
import { PreviewPathCard } from "./PreviewPathCard"
import { PreviewStepCard } from "./PreviewStepCard"
import { PathItems, PreviewItem } from "./types"
import { useCoverPagePreviewItems } from "./utils"

const CoverPagePathPreview = () => {
  const { galleryItems } = useCoverPagePreviewItems()
  const isMobile = useCoverPageContext((v) => v.isMobile)
  const previewSectionRef = useCoverPageContext((v) => v.previewSectionRef)
  const immersiveActive = useCoverPageContext((v) => v.immersiveActive)

  const maxItemHeight = isMobile ? "450px" : "600px"
  const computedItemsPerRow =
    useBreakpointValue({
      base: 1,
      md: 2
    }) ?? 1

  if (!galleryItems?.length) return null

  const isPathOnly =
    galleryItems.length === 1 && galleryItems[0].type === "path"

  return (
    <VStack position="relative" role="group" id="preview">
      {!immersiveActive && (
        <CoverPageDivider renderTitle={() => <PathPreviewNavigator />} />
      )}
      <CoverPageSectionTrigger sectionKey="preview" top="60px" />
      <HStack
        ref={previewSectionRef}
        className="CoverPagePathPreview"
        position="relative"
        role="group"
        w="100%"
        scrollMarginTop="150px"
        gap={0}
        h={isPathOnly ? "auto" : maxItemHeight}
        maxH="100%"
        css={{
          "& .carousel-item": {
            borderRadius: "card",
            maxWidth: isMobile
              ? `calc(100vw - ${COVER_PAGE_PADDING.md})`
              : "350px"
          },
          "& .gallery-container, & .carousel-scroller, & .carousel-track, & .carousel-item > .chakra-card":
            {
              height: "100%"
            }
        }}
      >
        {isPathOnly ? (
          <Box
            className="CoverPagePathPreview_PathOnly"
            px={
              isMobile
                ? `calc(${COVER_PAGE_PADDING.md} / 2)`
                : COVER_PAGE_PADDING
            }
            w="100%"
          >
            <Box
              maxH="100vh"
              overflowY="auto"
              h="100%"
              w="100%"
              borderRadius="card"
            >
              <PreviewPathCard data={galleryItems[0].data as PathItems} />
            </Box>
          </Box>
        ) : (
          <CarouselGallery
            items={galleryItems}
            itemsPerRow={computedItemsPerRow}
            maxItemHeight={maxItemHeight}
            itemsFillWidth={false}
            spacingY="0"
            spacingX={
              isMobile
                ? `calc(${COVER_PAGE_PADDING.md} / 2)`
                : COVER_PAGE_PADDING.md
            }
            scrollBehavior={isMobile ? "smooth" : "snap"}
            gap={20}
            enableStagger
            renderItem={(item: PreviewItem) => {
              const previewProps = {
                maxItemHeight,
              }
              const content =
                item.type === "path" ? (
                  <PreviewPathCard data={item.data} {...previewProps} />
                ) : (
                  <PreviewStepCard data={item.data} {...previewProps} />
                )

              return (
                <>
                  {content}
                  <Box
                    position="absolute"
                    bottom="10px"
                    left="50%"
                    transform="translateX(-50%)"
                    height="auto"
                    zIndex={10}
                  >
                    <ImmersiveTriggerButton
                      relativeTo={`preview/${
                        item.type === "path" ? "path" : item.data.source_id
                      }`}
                    />
                  </Box>
                </>
              )
            }}
            renderExpander={() => null}
          />
        )}
      </HStack>
    </VStack>
  )
}

export default CoverPagePathPreview
